import React, { FC, ReactNode } from 'react'
import { BLOCKS, Block, Inline, INLINES } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { kebabCase } from 'lodash'
import classNames from 'classnames'

import { IProps } from './index'
import style from './style.scss'
import { Text } from '../Text'

type Node = Block | Inline

const setOptions = (listStyle: string) => ({
  renderNode: {
    [BLOCKS.HEADING_1]: (node: Node, children: ReactNode) => (
      <h1 id={getId(children)}>
        <Text brand="default" size="heading-1-default">
          {children}
        </Text>
      </h1>
    ),
    [BLOCKS.HEADING_2]: (node: Node, children: ReactNode) => (
      <h2 id={getId(children)}>
        <Text brand="default" size="heading-2">
          {children}
        </Text>
      </h2>
    ),
    [BLOCKS.HEADING_3]: (node: Node, children: ReactNode) => (
      <h3 id={getId(children)}>
        <Text brand="default" size="heading-3">
          {children}
        </Text>
      </h3>
    ),
    [BLOCKS.HEADING_4]: (node: Node, children: ReactNode) => (
      <h4 id={getId(children)}>
        <Text brand="default" size="heading-4">
          {children}
        </Text>
      </h4>
    ),
    [BLOCKS.HEADING_5]: (node: Node, children: ReactNode) => (
      <h5 id={getId(children)}>
        <Text brand="default" size="heading-5">
          {children}
        </Text>
      </h5>
    ),
    [BLOCKS.HEADING_6]: (node: Node, children: ReactNode) => (
      <h6 id={getId(children)}>
        <Text brand="default" size="heading-6">
          {children}
        </Text>
      </h6>
    ),
    [BLOCKS.PARAGRAPH]: (node: Node, children: ReactNode) => {
      if (
        children &&
        Array.isArray(children) &&
        children.length <= 1 &&
        typeof children[0] === 'string' &&
        children[0].trim() === ''
      )
        return <br />
      return <p>{children}</p>
    },
    [INLINES.HYPERLINK]: (node: Node, children: ReactNode) => {
      const isInternalLink = node.data?.uri?.includes('intercom.com')
      return (
        <a
          id={getId(children)}
          href={node.data.uri}
          target={isInternalLink ? '_self' : '_blank'}
          rel={isInternalLink ? '' : 'noopener noreferrer'}
        >
          {children}
        </a>
      )
    },
    [BLOCKS.LIST_ITEM]: (node: Node, children: ReactNode) => {
      return (
        <li id={getId(children)} className={`list-item-${listStyle}`}>
          <Text brand="default" size="body">
            {children}
          </Text>
        </li>
      )
    },
  },
})

const getId = (children: ReactNode) => {
  if (Array.isArray(children) && typeof children[0] === 'string') {
    return kebabCase(children[0])
  }

  if (typeof children === 'string') {
    return kebabCase(children)
  }
}

const createHTMLWithKey = (html: string) => {
  return {
    __html: html,
  }
}

const render = (
  document: IProps['document'],
  html: IProps['html'],
  className: string,
  listStyle: string,
) => {
  if (document)
    return (
      <div className={className}>{documentToReactComponents(document, setOptions(listStyle))}</div>
    )
  /* eslint-disable react/no-danger */
  if (html) return <div className={className} dangerouslySetInnerHTML={createHTMLWithKey(html)} />
}

export const RichText: FC<IProps> = ({
  document,
  html,
  behaviour,
  listStyle = 'checkmark',
}: IProps) => {
  const className = classNames('rich-text', {
    [`rich-text__${behaviour?.toLowerCase()}`]: !!behaviour,
  })

  return (
    <>
      {render(document, html, className, listStyle)}
      <style jsx>{style}</style>
    </>
  )
}
